import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    signal,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {catchError, EMPTY, Subscription, switchMap, tap} from 'rxjs';
import {IRoleModel, IRolesGroups, TaskCollectionModel, TaskImages, TaskModel, UserTasksSettings} from '../../models/task.model';
import {ActivatedRoute, Router} from '@angular/router';
import {HelpersService} from '../../services/helpers.service';
import {AlertService} from '../../services/notify/alert.service';
import {ModalService} from '../../services/modal/modal.service';
import {MemberModel} from '../../models/user.model';
import {CrmService} from '../../services/api/crm.service';
import {
    NavigationTableRequestModel,
    NavigationTableResponseItemModel,
    NavigationTableResponseModel, PaginationModel,
} from '../../models/file-table.model';
import {MemberService} from '../../services/member.service';
import {DocumentsServiceService} from '../../controllers/documents/documents-service.service';
import {MemberPermissionsModel, NodeSettingsModel, StatusNodeModel, TaskBoardModel} from '../../models/tasktable.model';
import {TaskServiceService} from '../../services/task-service.service';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {DatePipe} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';
import {AppPinDialogComponentMT} from '../material/pin-dialog/app-pin-dialog.component';

interface RecommendedUsers {
    executor: UserWithMentions[];
    observer: UserWithMentions[];
    responsible: UserWithMentions[];
}

interface UserWithMentions {
    number_of_mentions_in_other_tasks: number;
    member: MemberModel;
}

@Component({
    selector: 'app-component-task-detail',
    templateUrl: './task-detail.component.html',
    styleUrls: ['./task-detail.component.scss'],
})
export class TaskDetailComponent implements OnInit {
    @Input() public taskAlias: string | null = null;
    @Input() public emitReloadTask: EventEmitter<string> = new EventEmitter<string>();
    @Input() public board: TaskBoardModel;

    @Output() emitOnLoad: EventEmitter<TaskModel | boolean> = new EventEmitter();
    @Output() emitOnDelete: EventEmitter<boolean> = new EventEmitter();
    @Output() emitUpdateStatus: EventEmitter<number> = new EventEmitter();
    @Output() emitUpdateTask: EventEmitter<string> = new EventEmitter();

    @ViewChild('viewDocument') public viewDocument: TemplateRef<any>;

    documentKey: string;
    public taskData: TaskModel;
    public importanceRange = [];
    public taskCollectionData: NavigationTableResponseModel;

    public currentUser: MemberModel;

    public tab = 0;

    step = signal(null);

    public currentEditing: string;
    public observeEditing = true;
    public canEdit = false;
    public observerCanEdit = false;
    public canCreateComments = false;
    public canEditProperties = false;

    public boardPermissions: MemberPermissionsModel;

    public isShowCollections = false;

    public allMembers: MemberModel[];

    public collectionId: number;
    public collectionName: string;

    public taskImportance = {
        NORMAL: {
            title: 'NORMAL',
            icon: 'icon-up-16px',
            color: 'table-light',
            textColor: ''
        },
        MIDDLE: {
            title: 'MIDDLE',
            icon: 'icon-up',
            color: 'table-warning',
            textColor: 'text-warning'
        },
        IMPORTANT: {
            title: 'IMPORTANT',
            icon: 'icon-up-1',
            color: 'table-danger',
            textColor: 'text-danger'
        },
    };

    boardAlias: string;
    availableTaskStatuses = {
        availableIn: [],
        availableOut: [],
    };

    taskTypes = [];

    recommendedUsers: RecommendedUsers = {
        executor: [],
        responsible: [],
        observer: [],
    };

    addMe: {
        executor: boolean,
        responsible: boolean,
        observer: boolean,
    } = {
        executor: false,
        responsible: false,
        observer: false,
    };
    selectImgUrl: SafeResourceUrl;
    selectedImage: Blob | MediaSource;
    selectedSubtask: TaskModel;
    childSubtasks: TaskModel[];
    childColons: UserTasksSettings[];
    parentSubtasks: TaskModel[];
    parentColons: UserTasksSettings[];
    isOpenAddImageModal = false;
    isLockButton = false;
    commentLength: number;

    imageScale = 1;

    pin: string = null;
    changingStatus: NodeSettingsModel;
    showRoles = false;

    private subscriptions: Subscription = new Subscription();
    private pinDialogRef: MatDialogRef<AppPinDialogComponentMT>;

    constructor(
        protected crmService: CrmService,
        protected alertService: AlertService,
        protected memberService: MemberService,
        public modalService: ModalService,
        protected router: Router,
        protected route: ActivatedRoute,
        public helper: HelpersService,
        public controller: DocumentsServiceService,
        protected taskService: TaskServiceService,
        private sanitizer: DomSanitizer,
        protected datePipe: DatePipe,
        public translate: TranslateService,
        private dialog: MatDialog,
        private changeDetectionRef: ChangeDetectorRef,
    ) {
    }

    @HostListener('document:click', ['$event'])
    clickObserve(event: Event): void {
        if (this.currentEditing && this.observeEditing) {
            if (!this[this.currentEditing].nativeElement.contains(event.target)) {
                this.applyEdit();
            }
        }
    }

    @HostListener('window: paste', ['$event'])
    pasteHandler($event: ClipboardEvent): void {
        if (this.tab !== 1) {
            return;
        }
        const items = $event.clipboardData.items;
        if (items) {
            let checkImage = false;
            for (const key of Object.keys(items)) {
                if (items[key].type.indexOf('image') !== -1) {
                    this.selectedImage = items[key].getAsFile();
                    this.viewImageUpload(this.selectedImage);
                    checkImage = true;
                }
            }
            if (!checkImage) {
                this.alertService.setAlert(this.translate.instant('ALERTS.NO_IMAGE'), 'blue');
            }
        }
    }

    ngOnInit(): void {
        if (this.taskAlias) {
            this.boardAlias = this.taskAlias.split('-')[0];
        }
        this.emitReloadTask.subscribe(() => {
            this.taskData = null;
            if (this.boardAlias) {
                void this.getTaskData();
            }
        });
        this.memberService.getCurrentMember().then((result: MemberModel) => {
            this.currentUser = result;
        });
        this.memberService.getAllMembers().then(res => {
            this.allMembers = res.filter(x => x.project_setting.observation_type !== 'blocked');
        });
        this.route.params.subscribe(res => {
            if (res.alias) {
                this.boardAlias = res.alias;
            }

            if (res.taskAlias && (this.taskAlias !== res.taskAlias)) {
                this.taskAlias = res.taskAlias;
                this.taskData = null;
            }
        });
        void this.getTaskData();
    }

    openStep(value): void {
        this.step.set(value);
    }

    closeStep(value): void {
        if (value === this.step()) {
            this.step.set(null);
        }
    }

    onOpenModal(content: TemplateRef<any>, size?: string): void {
        this.modalService.openTemplateModal(content, size);
    }

    async getTaskData(): Promise<void> {
        this.showRoles = false;
        this.crmService.getTask(this.taskAlias).subscribe({
            next: data => {
                this.taskData = data.task;

                let membersGuids: string[] = [];
                Object.values(this.taskData.roles).forEach((members: any) => {
                    let membersRoleGuids: string[] = [];
                    members.forEach((member: IRoleModel) => {
                        membersRoleGuids.push(member.user_guid);
                    });

                    membersGuids = membersGuids.concat(membersRoleGuids);
                });
                membersGuids.push(this.taskData.author_guid);

                this.memberService.memberGuidsHandler(membersGuids).then(members => {
                    this.taskData.author = members.find(x => x.guid === this.taskData.author_guid);

                    Object.keys(this.taskData.roles).forEach((role: string) => {
                        this.taskData.roles[role] = this.taskData.roles[role].map((memberRole: IRoleModel) => {
                            memberRole.memberModel = members.find(member => member.guid === memberRole.user_guid);
                            return memberRole;
                        });
                    });
                    this.showRoles = true;
                    this.afterLoadTask();
                });

                this.importanceRange = [];
                this.boardPermissions = data.rights;

                for (let i = 1; i <= data.importance_indexes[1]; i++) {
                    this.importanceRange.push(i);
                }

                if (this.tab === 1) {
                    this.getTaskImage();
                }
                this.getSubtasks();
            }, error: () => {
                this.emitOnLoad.emit(false);
                if (window.location.pathname.split('/').includes(this.boardAlias)) {
                    this.router.navigate(['account', 'tasks', 'all']);
                }
            },
        });
    }

    changeTab(index: number): void {
        this.tab = index;
        if (index === 1) {
            this.getTaskImage();
        }
    }

    async getSubtasks(): Promise<void> {
        this.crmService.getSubtasks(this.taskData.task_alias).subscribe(res => {
            this.childSubtasks = res.subtasks;
            this.childColons = res.colons;
        });

        this.crmService.getParentsTasks(this.taskData.task_alias).subscribe(resp => {
            this.parentSubtasks = resp.parent_tasks;
            this.parentColons = resp.colons;
            this.parentSubtasks.map((x) => x.isParentSubtask = true);
        });
    }

    afterLoadTask(): void {
        this.availableTaskStatuses = {
            availableIn: [],
            availableOut: [],
        };
        this.taskTypes = [];

        this.crmService.recommendedUsersForTask(this.boardAlias).subscribe(users => {
            if (this.taskData.roles.executor.length) {
                for (const user of this.taskData.roles.executor) {
                    this.recommendedUsers.executor = users.executor = users.executor.filter(x =>
                        x.member.guid !== user.user_guid).slice(0, 3);
                }
            } else {
                this.recommendedUsers.executor = users.executor.slice(0, 3);
            }
            if (this.taskData.roles.responsible.length) {
                for (const user of this.taskData.roles.responsible) {
                    this.recommendedUsers.responsible = users.responsible = users.responsible.filter(x =>
                        x.member.guid !== user.user_guid).slice(0, 3);
                }
            } else {
                this.recommendedUsers.responsible = users.responsible.slice(0, 3);
            }
            if (this.taskData.roles.observer.length) {
                for (const user of this.taskData.roles.observer) {
                    this.recommendedUsers.observer = users.observer = users.observer.filter(x =>
                        x.member.guid !== user.user_guid).slice(0, 3);
                }
            } else {
                this.recommendedUsers.observer = users.observer.slice(0, 3);
            }
        });

        this.taskService.getBoard(this.boardAlias, !this.board).then(res => {
            this.board = res.board;
            if (this.taskData.status_current.is_universal) {
                this.statusFilterUniversal();
            } else {
                this.statusFilter();
            }
        });

        for (const element of this.taskData.available_task_types) {
            this.taskTypes.push({
                key: element.id,
                title: element.name,
                icon: element.icon,
            });
        }
        const currentExecutor = this.taskData.roles.executor.find(
            (executors: IRoleModel) => executors.user_guid === this.currentUser.guid);
        const currentResponsible = this.taskData.roles.responsible.find(
            (responsible: IRoleModel) => responsible.user_guid === this.currentUser.guid);
        const currentObserver = this.taskData.roles.observer.find(
            (observer: IRoleModel) => observer.user_guid === this.currentUser.guid);
        this.canEdit = (this.taskData.author_guid === this.currentUser.guid ||
                this.currentUser.project_setting.is_project_admin) ||
            !!currentExecutor || !!currentResponsible;
        this.observerCanEdit = !!currentObserver;

        this.addMe = {
            executor: currentExecutor && (this.boardPermissions.is_set_self_in_task === 'allowed' ||
                this.currentUser.project_setting.is_project_admin),
            responsible: currentResponsible && (this.boardPermissions.is_set_self_in_task === 'allowed' ||
                this.currentUser.project_setting.is_project_admin),
            observer: currentObserver && (this.boardPermissions.is_set_self_in_task === 'allowed' ||
                this.currentUser.project_setting.is_project_admin),
        };
        // !this.taskData.roles.observer.length &&
        // !this.taskData.roles.responsible.length && !this.taskData.roles.executor.length && this.boardPermissions.is_set_self_in_task;

        this.canCreateComments = this.boardPermissions.is_create_new_comment === 'allowed' ||
            this.currentUser.project_setting.is_project_admin;
        this.canEditProperties = this.currentUser.project_setting.is_project_admin ||
            this.boardPermissions.is_set_property_val === 'allowed';

        this.taskData.comments = this.taskData.comments ? this.taskData.comments : [];

        this.wrapCollections();
        this.emitOnLoad.emit(this.taskData);
    }

    onLoadCommentsCount(count: number): void {
        this.commentLength = count;
    }

    statusFilterUniversal(): void {
        const statuses: NodeSettingsModel[] = this.board.status_node_settings_of_member.filter(x =>
            x.status_node.is_universal && x.status_node.id !== this.taskData.status_current.id || x.status_node.is_initial);
        for (const status of statuses) {
            this.availableTaskStatuses.availableOut.push({
                key: status.status_node.id,
                title: status.status_node.label,
                icon: status.status_node.data.svg.class,
                isIn: false,
                is_successful: status.status_node.is_successful,
            });
        }
    }

    statusFilter(): void {
        const dependencies = {
            in: this.board.status_edges.filter((x: any) => x.target.toString() === this.taskData.status_current.id.toString()),
            out: this.board.status_edges.filter((x: any) => x.source.toString() === this.taskData.status_current.id.toString()),
        };
        for (const element of dependencies.in) {
            const status: NodeSettingsModel = this.board.status_node_settings_of_member.filter(
                x => x.status_node.id.toString() === element.source.toString())[0];
            this.availableTaskStatuses.availableIn.push({
                key: status.status_node.id,
                title: status.status_node.label,
                icon: status.status_node.data.svg.class,
                isIn: true,
                is_successful: status.status_node.is_successful,
                canChange: !!this.taskData.roles.responsible.filter(x => x.user_guid === this.currentUser.guid)[0]
                    || (this.currentUser.project_setting.is_project_admin
                        || this.currentUser.project_setting.observation_type === 'dep_admin'),
            });
        }

        for (const element of dependencies.out) {
            const status: NodeSettingsModel = this.board.status_node_settings_of_member.filter(
                x => x.status_node.id.toString() === element.target.toString())[0];
            this.availableTaskStatuses.availableOut.push({
                key: status.status_node.id,
                title: status.status_node.label,
                icon: status.status_node.data.svg.class,
                isIn: false,
                is_successful: status.status_node.is_successful,
                canChange: !!this.taskData.roles.responsible.filter(x => x.user_guid === this.currentUser.guid)[0]
                    || (this.currentUser.project_setting.is_project_admin
                        || this.currentUser.project_setting.observation_type === 'dep_admin'),
            });
        }

        if (this.board.status_node_settings_of_member.filter(x => x.status_node.is_universal).length
            && !this.taskData.status_current.is_finish) {
            const universalStatuses = this.board.status_node_settings_of_member.filter(x => x.status_node.is_universal);
            for (const universalStatus of universalStatuses) {
                this.availableTaskStatuses.availableOut.push({
                    key: universalStatus.status_node.id,
                    title: universalStatus.status_node.label,
                    icon: universalStatus.status_node.data.svg.class,
                    isIn: false,
                });
            }
        }

        this.availableTaskStatuses.availableOut = this.availableTaskStatuses.availableOut.sort((n1, n2) => {
            const arg1 = n1.title;
            const arg2 = n2.title;
            if (arg1 > arg2) {
                return 1;
            } else {
                return -1;
            }
        });

        this.availableTaskStatuses.availableIn = this.availableTaskStatuses.availableIn.sort((n1, n2) => {
            const arg1 = n1.title;
            const arg2 = n2.title;
            if (arg1 > arg2) {
                return 1;
            } else {
                return -1;
            }
        });
    }

    wrapCollections(): void {
        this.taskCollectionData = new NavigationTableResponseModel();
        this.taskCollectionData.data = this.taskData.collections ? this.taskData.collections : [];
    }

    editTaskData(key: string, value: string | number | Date): void {
        if (key === 'duration' && value === '0') {
            return this.alertService.setAlert(this.translate.instant('ALERTS.TIME_ERROR'), 'blue');
        }

        if (key === 'due_time') {
            value = this.datePipe.transform(value, 'yyyy-MM-ddTHH:mm:ss') + 'Z';
        }

        if (!value && typeof value !== 'string') {
            return;
        }
        if (value === this.taskData[key]) {
            return;
        }
        if (key === 'deadline_at') {
            value = this.helper.dateFromFormat(value as string);
            value = (new Date(value).getFullYear() + '-' + (new Date(value).getUTCMonth() + 1) + '-' + new Date(value).getDate());
        }

        const data: any = {
            title: this.taskData.title,
            content: this.taskData.content,
            deadline_at: this.taskData.deadline_at ? this.taskData.deadline_at.substring(0, 10) : null,
            importance: this.taskData.importance,
            task_type_id: this.taskData.task_type.id,
            due_time: this.taskData.due_time ? (this.taskData.due_time.replace(' ', 'T') + 'Z') : null,
            duration: this.taskData.duration,
            importance_index: this.taskData.importance_index,
        };
        data[key] = value;

        this.updateTaskData(key, value);
        this.crmService.editTask(this.taskData.task_alias, data).subscribe({
            next: res => {
                void this.getTaskData();
                this.alertService.setAlert(res.message, 'green');
                this.emitUpdateTask.emit();
            }, error: error => {
                this.alertService.setAlert(error.message, 'blue');
            },
        });
    }

    editTaskStatus(value: number, modal = false): void {
        this.changingStatus = this.board.status_node_settings_of_member.filter(x => x.status_node.id === value)[0];

        if (this.changingStatus.status_node.is_successful && this.taskData.contract_guid && !modal) {
            if (this.currentUser.project_setting.is_project_admin ||
                this.currentUser.project_setting.observation_type === 'dep_admin' ||
                this.taskData.user_roles.includes('responsible')
            ) {
                const isSign = true;
                this.pinDialogRef = this.dialog.open(AppPinDialogComponentMT);
                this.pinDialogRef.componentInstance.isSign = isSign;
                this.pinDialogRef.componentInstance.header = this.translate.instant('MODALS.HEADERS.CONFIRM_TASK_STATUS');

                this.subscriptions.add(
                    this.pinDialogRef.afterClosed()
                        .pipe(
                            tap(() => {
                                this.changeDetectionRef.detectChanges();
                            }),
                            switchMap((pin: { pin: string }): null => {
                                if (pin.pin) {
                                    this.pin = pin.pin;
                                    this.editTaskStatus(this.changingStatus.status_node.id, true);
                                    return;
                                }
                                return;
                            }), catchError(() => {
                                this.changeDetectionRef.detectChanges();
                                return EMPTY;
                            })
                        ).subscribe());
                return;
            } else {
                this.alertService.setAlert(this.translate.instant('ALERTS.CHANGE_STATUS_PERMISSION'), 'blue');
                return;
            }
        }

        this.setStatus(this.changingStatus.status_node, this.pin);
    }

    setStatus(status: StatusNodeModel, pin?: string): void {
        this.crmService.updateTaskStatus(this.taskData.task_alias, status.id, pin).subscribe({
            next: res => {
                void this.getTaskData();
                this.alertService.setAlert(res.message, 'green');
                this.emitUpdateStatus.emit(status.id);
                this.changingStatus = undefined;
                this.pin = null;
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
                this.pin = null;
            }
        });
    }

    onEditContent(event: [boolean, string]): void {
        if (event[0]) {
            this.editTaskData('content', event[1]);
        }
    }

    onEditTitle(event: string): void {
        this.editTaskData('title', event);
    }

    applyEdit(): void {
        this.editTaskData(this.currentEditing, this[this.currentEditing].nativeElement.value);
        this.currentEditing = '';
        this.observeEditing = false;
    }

    addEmployee(member: string, roleInTask: string, modal: NgbModalRef = null): void {
        const data = {
            guid: member,
            role: roleInTask,
        };

        if (!data.guid) {
            this.alertService.setAlert(this.translate.instant('ALERTS.SET_MEMBER'), 'blue');
            return;
        }
        if (!data.role) {
            this.alertService.setAlert(this.translate.instant('ALERTS.SET_ROLE'), 'blue');
            return;
        }

        for (const user of this.taskData.roles[data.role]) {
            if (user.guid === data.guid) {
                return this.alertService.setAlert(this.translate.instant('ALERTS.SET_USER_TASK'), 'blue');
            }
        }
        this.employeeRequest(this.taskData.task_alias, data, modal);
    }

    selectCollection(collection: NavigationTableResponseItemModel): void {
        if (collection) {
            this.collectionId = collection.id;
        }
    }

    addCollection(modal: NgbModalRef): void {
        this.crmService.addCollectionInTask(this.taskData.task_alias, this.collectionId).subscribe({
            next: res => {
                modal.close();
                this.isShowCollections = false;
                this.alertService.setAlert(res.message, 'green');
                this.getCollections();
            }, error: () => {
                modal.close();
            },
        });
    }

    employeeRequest(taskAlias: string, userData: { guid: string; role: string; }, modal: NgbModalRef = null): void {
        this.isLockButton = true;
        this.crmService.addUserInTask(taskAlias, userData).subscribe({
            next: data => {
                this.alertService.setAlert(data.message, 'green');
                void this.getTaskData();
                if (modal) {
                    modal.close();
                }
                this.isLockButton = false;
                this.emitUpdateTask.emit();
            }, error: error => {
                if (userData.guid === this.currentUser.guid) {
                    this.addMe[userData.role] = false;
                }
                this.alertService.setAlert(error.message, 'blue');
                this.isLockButton = false;
            },
        });
    }

    deleteEmployee(user: MemberModel, currentRole: string): void {
        let modalSettings: any;
        modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE_MEMBER'),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.DELETE'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                this.crmService.deleteUserFromTask(this.taskData.task_alias, {
                    guid: user.guid,
                    role: currentRole,
                }).subscribe(res => {
                    this.alertService.setAlert(res.message, 'green');
                    void this.getTaskData();
                    this.emitUpdateTask.emit();
                });
            } else {
                if (user.guid === this.currentUser.guid) {
                    this.addMe[currentRole] = !this.addMe[currentRole];
                }
            }
        }).catch(() => {
            if (user.guid === this.currentUser.guid) {
                this.addMe[currentRole] = !this.addMe[currentRole];
            }
            return;
        });
    }

    deleteTask(): void {
        let modalSettings: any;
        modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE_TASK'),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.DELETE'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                this.crmService.deleteTask(this.taskData.task_alias).subscribe({
                    next: res => {
                        this.alertService.setAlert(res.message, 'green');
                        this.emitOnDelete.emit(true);
                    }, error: error => {
                        this.alertService.setAlert(error.message, 'blue');
                        this.emitOnDelete.emit(false);
                    },
                });
            }
        }).catch(() => {
            return;
        });
    }

    getCollections(): void {
        this.crmService.getTaskCollections(this.taskData.task_alias).subscribe(res => {
            this.taskData.collections = res.data;
            this.wrapCollections();
        });
    }

    deleteCollection(collectionId: number): void {
        let modalSettings: any;
        modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE_COLLECTION'),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.DELETE'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                this.crmService.deleteCollectionFromTask(this.taskData.task_alias, collectionId).subscribe(res => {
                    this.isShowCollections = false;
                    this.getCollections();
                    this.alertService.setAlert(res.message, 'green');
                });
            }
        }).catch(() => {
            return;
        });
    }

    profile(user: MemberModel, event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        this.helper.openProfileByGuid(user.guid);
    }

    updateTaskData(key: string, value: string | number | Date): void {
        switch (key) {
            case 'status':
                this.taskData[key] = value;
                break;
            default:
                this.taskData[key] = value;
                this.taskData.updated_at = new Date().toString();
                break;
        }
    }

    getViewCollections(collection: TaskCollectionModel): void {
        this.isShowCollections = true;
        this.collectionName = collection.name;
        const request: NavigationTableRequestModel = new NavigationTableRequestModel();
        request.filter = 'collection';
        request.id = collection.id;
        request.limit = 15;
        this.controller.loadNav(this.controller.prepare(request));
    }

    onEventFind(event: NavigationTableRequestModel | PaginationModel): void {
        this.controller.loadNav(this.controller.prepare(event));
    }

    onEventClick(event: NavigationTableResponseItemModel): void {
        this.documentKey = event.key;
        this.modalService.openTemplateModal(this.viewDocument, 'xl');
    }

    uploadImage(modal: NgbModalRef = null): void {
        const image = {
            photo: this.selectedImage,
        };
        this.isLockButton = true;
        this.crmService.addImageToTask(this.taskData.task_alias, image).subscribe({
            next: () => {
                this.selectedImage = null;
                if (modal) {
                    modal.close();
                }
                void this.getTaskData();
                this.isLockButton = false;
            }, error: error => {
                this.isLockButton = false;
                this.alertService.setAlert(error, 'blue');
            },
        });
    }

    viewImageUpload(data: Blob | MediaSource, preview = false): void {
        this.selectedImage = data;
        const output = document.getElementById('output');
        const src = 'src';
        if (output) {
            output[src] = URL.createObjectURL(data);
            output.onload = () => {
                URL.revokeObjectURL(output[src]);
            };
        }
        if (!preview && !this.isOpenAddImageModal) {
            this.uploadImage();
        }
    }

    deleteImage(image: TaskImages): void {
        let modalSettings: any;
        modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE_IMAGE'),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                this.crmService.deleteImageInTask(this.taskData.task_alias, image.ukey).subscribe({
                    next: res => {
                        this.alertService.setAlert(res.message, 'green');
                        void this.getTaskData();
                    }, error: error => {
                        this.alertService.setAlert(error.message, 'blue');
                    },
                });
            }
        }).catch(() => {
            return;
        });
    }

    setSubtask(modal: NgbModalRef): void {
        this.crmService.addSubtask(this.taskData.task_alias, this.selectedSubtask.task_alias).subscribe({
            next: res => {
                modal.close();
                void this.getSubtasks();
                this.alertService.setAlert(res.message, 'green');
            }, error: error => {
                this.alertService.setAlert(error.message, 'blue');
            },
        });
    }

    getTaskImage(errorImage?: TaskImages, errorCount = 0): any {
        let gettingImages: TaskImages[] = [];
        if (!errorImage) {
            gettingImages = this.taskData.photos;
        } else {
            gettingImages.push(errorImage);
        }
        gettingImages.forEach(img => {
            if (img.image) {
                return;
            }
            this.crmService.getTaskImage(this.taskAlias, img.ukey).subscribe({
                next: res => {
                    img.image = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(res));
                }, error: () => {
                    errorCount += 1;
                    if (errorCount === 5) {
                        return;
                    }
                    setTimeout(() => {
                        this.getTaskImage(img, errorCount);
                    }, 2000);
                }
            });
        });
    }

    changeZoom(): void {
        const img = document.getElementById('zoomImg');

        if (img.style.transform.split('scale').length > 1) {
            img.style.transform = img.style.transform.split('scale')[0] + 'scale(' + this.imageScale + ')';
        } else {
            img.style.transform = img.style.transform + 'scale(' + this.imageScale + ')';
        }

        if (this.imageScale === 1) {
            img.style.transform = 'scale(1)';
        }
    }

    @HostListener('document:mousewheel', ['$event', '$event'])
    protected onScroll(target: Event, event: WheelEventInit): void {
        const img = document.getElementById('zoomImg');
        const imgContainer = document.getElementById('imgContainer');
        if (target.target === img || target.target === imgContainer) {
            if (event.deltaY < 0) {
                this.imageScale += 1;
            } else {
                if (this.imageScale !== 1) {
                    this.imageScale -= 1;
                }
            }
            this.changeZoom();
        }
    }

    changeMyRole(role: string, check: boolean): void {
        if (check) {
            this.addEmployee(this.currentUser.guid, role);
        } else {
            this.deleteEmployee(this.currentUser, role);
        }
    }

    deleteTaskFormSubtasks(taskAlias: string): void {
        const modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE'),
            modalBody: this.translate.instant('CONFIRM.TEXT.DELETE_CHILD_TASK'),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                this.crmService.deleteSubtask(this.taskData.task_alias, taskAlias).subscribe({
                    next: res => {
                        this.alertService.setAlert(res.message, 'green');
                        void this.getSubtasks();
                    }, error: error => {
                        this.alertService.setAlert(error.message, 'blue');
                    },
                });
            }
        }).catch(() => {
            return;
        });
    }
}
